import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, MapPin, Maximize2, Minimize2 } from 'lucide-react';
import { GoogleMap, useJsApiLoader, Marker, Polyline, OverlayView } from '@react-google-maps/api';
import { avatarSrc } from '../avatar/avatarList';
import { useAuth } from '../../hooks/useAuth';
import './GameRound.css';

const NEPAL_CENTER = { lat: 28.3949, lng: 84.1240 };

// Walking is core gameplay, but every step loads a new billable Street View
// panorama. Cap moves per round so normal exploration is unaffected while a
// griefer can't rack up hundreds of loads. Tune to taste.
const MAX_PANO_MOVES = 30;

const GameRound = ({
  roundNumber,
  totalRounds,
  dropLocation,
  actualLocation,
  hints = [],
  onGuess,
  isSubmitting,
  result,
  resultGuesses = null,
  onContinue,
  onHintRevealed,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [guess, setGuess] = useState(null);
  const [showingResult, setShowingResult] = useState(false);
  // Staggered reveal of the result map: guess avatars drop first, then the answer.
  const [pinsVisible, setPinsVisible] = useState(false);
  const [answerVisible, setAnswerVisible] = useState(false);
  const [currentHintIndex, setCurrentHintIndex] = useState(-1);
  const [hintOpen, setHintOpen] = useState(false);
  // Tap-to-enlarge the minimap on touch devices (where :hover can't grow it).
  const [mapExpanded, setMapExpanded] = useState(false);
  const panoramaRef = useRef(null);
  const resultMapRef = useRef(null);
  const moveCountRef = useRef(0);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  useEffect(() => {
    if (result) {
      setShowingResult(true);
    } else {
      setShowingResult(false);
      setGuess(null);
    }
  }, [result]);

  // Hold the pins back for a beat, then drop the guesses in, then the answer —
  // so the reveal feels like a reveal instead of everything snapping on at once.
  useEffect(() => {
    setPinsVisible(false);
    setAnswerVisible(false);
    if (!result) return undefined;
    const t1 = setTimeout(() => setPinsVisible(true), 400);
    const t2 = setTimeout(() => setAnswerVisible(true), 950);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [result]);

  useEffect(() => {
    setCurrentHintIndex(-1);
    setHintOpen(false);
    setMapExpanded(false);
    moveCountRef.current = 0;
  }, [dropLocation]);

  useEffect(() => {
    if (isLoaded && dropLocation && panoramaRef.current && !showingResult) {
      const svOptions = {
        pov: { heading: 165, pitch: 0 },
        zoom: 0,
        addressControl: false,
        showRoadLabels: false,
        enableCloseButton: false,
        fullscreenControl: false,
        linksControl: true,
        panControl: true,
        zoomControl: false,
      };

      // Create the panorama and cap how far the player can walk. Each move loads
      // a new billable panorama; once MAX_PANO_MOVES is hit we disable navigation
      // so the round can't keep loading panoramas indefinitely.
      const makePano = (extra) => {
        const pano = new window.google.maps.StreetViewPanorama(panoramaRef.current, { ...svOptions, ...extra });
        let initialised = false;
        pano.addListener('pano_changed', () => {
          if (!initialised) { initialised = true; return; } // skip the first (initial load) event
          moveCountRef.current += 1;
          if (moveCountRef.current >= MAX_PANO_MOVES) {
            pano.setOptions({ linksControl: false, clickToGo: false });
          }
        });
        return pano;
      };

      if (dropLocation.pano_id) {
        makePano({ pano: dropLocation.pano_id });
        return;
      }

      const pos = { lat: parseFloat(dropLocation.latitude), lng: parseFloat(dropLocation.longitude) };
      const svService = new window.google.maps.StreetViewService();
      const radii = [1000, 5000, 10000, 50000];
      let attempt = 0;

      const tryNext = () => {
        if (!panoramaRef.current) return;
        if (attempt >= radii.length) {
          makePano({ position: pos });
          return;
        }
        svService.getPanorama({ location: pos, radius: radii[attempt] }, (data, status) => {
          if (status === 'OK' && panoramaRef.current) {
            makePano({ pano: data.location.pano });
          } else {
            attempt++;
            tryNext();
          }
        });
      };

      tryNext();
    }
  }, [isLoaded, dropLocation, showingResult]);

  const handleMapClick = (e) => {
    if (!showingResult) {
      setGuess({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    }
  };

  const handleSubmit = () => {
    if (guess && !isSubmitting) {
      onGuess(guess.lat, guess.lng);
    }
  };

  const handleContinue = () => {
    setShowingResult(false);
    setGuess(null);
    setCurrentHintIndex(-1);
    setHintOpen(false);
    if (onContinue) onContinue();
  };

  const handleLogoClick = () => {
    if (window.confirm('Leave the game? Your progress will be saved.')) {
      navigate('/dashboard');
    }
  };

  const revealNextHint = () => {
    if (currentHintIndex < hints.length - 1) {
      setCurrentHintIndex(currentHintIndex + 1);
      if (onHintRevealed) onHintRevealed();
    }
  };

  if (!isLoaded) {
    return (
      <div className="loading-fullscreen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (showingResult && result) {
    const guessLat = parseFloat(result.guess_location?.latitude);
    const guessLng = parseFloat(result.guess_location?.longitude);
    const actualLat = parseFloat(result.actual_location?.latitude);
    const actualLng = parseFloat(result.actual_location?.longitude);

    const hasOwnGuess = !isNaN(guessLat) && !isNaN(guessLng);

    // Every guess on the result map is shown with the player's avatar. In
    // multiplayer that's every participant (from resultGuesses); in solo it's
    // just the current user's own drop.
    const rawGuesses = (resultGuesses && resultGuesses.length > 0)
      ? resultGuesses
      : (hasOwnGuess
          ? [{
              user_id: user?.id ?? 'you',
              user_name: user?.name ?? 'You',
              avatar: user?.avatar,
              guess_latitude: guessLat,
              guess_longitude: guessLng,
              is_current_user: true,
              is_timeout: false,
            }]
          : []);

    const avatarGuesses = rawGuesses
      .map((g) => {
        const lat = parseFloat(g.guess_latitude);
        const lng = parseFloat(g.guess_longitude);
        if (g.is_timeout || isNaN(lat) || isNaN(lng)) return null;
        return { ...g, pos: { lat, lng } };
      })
      .filter(Boolean);
    const showAvatarGuesses = avatarGuesses.length > 0;

    // Need the actual location always; without any guess there's nothing to draw.
    if (isNaN(actualLat) || isNaN(actualLng) || (!hasOwnGuess && !showAvatarGuesses)) {
      console.error('Invalid coordinates:', { guessLat, guessLng, actualLat, actualLng });
      return null;
    }

    const actualPos = { lat: actualLat, lng: actualLng };

    // The educational popup (single player) also hosts the score + Next button,
    // so the minimap's own continue button is only needed when there's no popup.
    const showEduCard = !!(result.actual_location?.description || result.actual_location?.facts?.length > 0);

    return (
      <div className="geoguessr-game">
        <div className="street-view-fullscreen" ref={panoramaRef} />

        <button className="brand-logo" onClick={() => navigate('/dashboard')} aria-label="MapHunt — back to dashboard">
          <img src="/logo/logo.png" alt="" aria-hidden="true" />
        </button>

        {showEduCard && (
          <div className="round-edu-card">
            <div className="edu-card-head">
              <MapPin size={20} />
              <span className="edu-card-name">{result.actual_location.name}</span>
            </div>
            {result.actual_location.description && (
              <p className="edu-card-desc">{result.actual_location.description}</p>
            )}
            {result.actual_location.facts?.length > 0 && (
              <ul className="edu-card-facts">
                {result.actual_location.facts.map((fact, i) => (
                  <li key={i}>
                    <Lightbulb size={12} />
                    <span>{fact}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="edu-card-footer">
              <div className="edu-score">
                <span className="edu-score-pts">{result.score?.toLocaleString() || 0} pts</span>
                <span className="edu-score-dist">{parseFloat(result.distance_km || 0).toFixed(1)} km away</span>
              </div>
              <button className="edu-next-btn" onClick={handleContinue}>
                {roundNumber < totalRounds ? 'Next Round →' : 'See Results →'}
              </button>
            </div>
          </div>
        )}

        <div className={`minimap-bare result-view${mapExpanded ? ' expanded' : ''}`}>
          <button
            type="button"
            className="minimap-expand-toggle"
            onClick={() => setMapExpanded((v) => !v)}
            aria-label={mapExpanded ? 'Shrink map' : 'Enlarge map'}
          >
            {mapExpanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
          <GoogleMap
            key={`result-${roundNumber}-${actualLat}-${actualLng}`}
            mapContainerClassName="minimap"
            onLoad={(map) => {
              resultMapRef.current = map;
              // Frame the guess(es) and the answer together. Left uncontrolled
              // (no center/zoom props) so later re-renders from the staggered
              // reveal don't snap the viewport back and crop a marker out.
              const fit = () => {
                const bounds = new window.google.maps.LatLngBounds();
                bounds.extend(actualPos);
                avatarGuesses.forEach((g) => bounds.extend(g.pos));
                map.fitBounds(bounds, 40);
              };
              map.setCenter(actualPos); // avoid a world-view flash before the fit
              setTimeout(fit, 60);
            }}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              zoomControl: false,
              gestureHandling: 'greedy',
            }}
          >
            {/* Distance lines draw once the answer drops, joining each guess to it. */}
            {answerVisible && avatarGuesses.map((g) => (
              <Polyline
                key={`line-${g.user_id}`}
                path={[g.pos, actualPos]}
                options={{
                  strokeColor: g.is_current_user ? '#14B8A6' : '#FC4C3C',
                  strokeOpacity: 0.75,
                  strokeWeight: 2,
                  geodesic: false,
                  zIndex: 1,
                }}
              />
            ))}

            {/* Guess avatars drop in first, staggered. */}
            {pinsVisible && avatarGuesses.map((g, i) => (
              <OverlayView
                key={`pin-${g.user_id}`}
                position={g.pos}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={() => ({ x: -19, y: -19 })}
              >
                <div
                  className={`map-avatar-pin map-avatar-drop${g.is_current_user ? ' map-avatar-pin--you' : ''}`}
                  style={{ animationDelay: `${i * 110}ms` }}
                  title={g.user_name}
                >
                  <div className="map-avatar-img">
                    {g.avatar
                      ? <img src={avatarSrc(g.avatar)} alt={g.user_name} />
                      : <span>{g.user_name?.charAt(0).toUpperCase()}</span>}
                  </div>
                </div>
              </OverlayView>
            ))}

            {/* The answer pin drops last, with the native bounce. */}
            {answerVisible && (
              <Marker
                position={actualPos}
                animation={window.google.maps.Animation.DROP}
                icon={{
                  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
                      <path d="M16 0C7.2 0 0 7.2 0 16c0 8.8 16 24 16 24s16-15.2 16-24C32 7.2 24.8 0 16 0z" fill="#2ECC71" stroke="#fff" stroke-width="2"/>
                      <circle cx="16" cy="16" r="6" fill="#fff"/>
                    </svg>
                  `),
                  scaledSize: new window.google.maps.Size(32, 40),
                  anchor: new window.google.maps.Point(16, 40),
                }}
                zIndex={3}
              />
            )}
          </GoogleMap>

          {!showEduCard && (
            <button className="continue-min" onClick={handleContinue}>
              {result.score?.toLocaleString() || 0} pts · {parseFloat(result.distance_km || 0).toFixed(1)} km
              <span className="continue-arrow">{roundNumber < totalRounds ? 'Next →' : 'Results →'}</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  const hasHints = hints && Array.isArray(hints) && hints.length > 0;

  return (
    <div className="geoguessr-game">
      <div className="street-view-fullscreen" ref={panoramaRef} />

      <button className="brand-logo" onClick={handleLogoClick} aria-label="MapHunt — back to dashboard">
        <img src="/logo/logo.png" alt="" aria-hidden="true" />
        <span>Map<span className="brand-accent">Hunt</span></span>
      </button>

      {hasHints && (
        <div className="hint-mini">
          <button
            className="hint-btn"
            onClick={() => setHintOpen((v) => !v)}
            aria-label="Hint"
            aria-expanded={hintOpen}
          >
            <Lightbulb size={22} />
            {currentHintIndex >= 0 && (
              <span className="hint-count">{currentHintIndex + 1}/{hints.length}</span>
            )}
          </button>

          {hintOpen && (
            <div className="hint-pop">
              {currentHintIndex >= 0 ? (
                <p className="hint-text">{hints[currentHintIndex]}</p>
              ) : (
                <p className="hint-text muted">No hint yet.</p>
              )}
              {currentHintIndex < hints.length - 1 && (
                <button className="hint-reveal" onClick={revealNextHint}>
                  {currentHintIndex < 0 ? 'Reveal hint' : `Next (${currentHintIndex + 2}/${hints.length})`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className={`minimap-bare${mapExpanded ? ' expanded' : ''}`}>
        <button
          type="button"
          className="minimap-expand-toggle"
          onClick={() => setMapExpanded((v) => !v)}
          aria-label={mapExpanded ? 'Shrink map' : 'Enlarge map'}
        >
          {mapExpanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
        </button>
        <GoogleMap
          mapContainerClassName="minimap"
          center={guess || NEPAL_CENTER}
          zoom={7}
          onClick={handleMapClick}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            zoomControl: false,
            gestureHandling: 'greedy',
          }}
        >
          {guess && <Marker position={guess} />}
        </GoogleMap>

        <button
          className={`guess-min ${guess ? 'active' : ''}`}
          onClick={handleSubmit}
          disabled={!guess || isSubmitting}
        >
          {isSubmitting ? 'Submitting…' : guess ? 'Guess →' : 'Pick a spot'}
        </button>
      </div>
    </div>
  );
};

export default GameRound;
